import { useEffect, useState } from "react";
import { OptionSide, TURN_WINDOW_MS } from "@badorbad/shared";
import { AvatarFace } from "../design/AvatarFace";
import { Split } from "../design/Split";
import { TimerBar } from "../design/TimerBar";
import { nameColorFor } from "../design/seed";
import { Cue, play } from "../design/sound";
import { NAME_COLORS, OptionSideKey, palette } from "../design/tokens";
import { t, fmt } from "../locale";
import { useGame } from "../stores/game";

/**
 * The turn. One decision, two options, and two entirely different jobs on the
 * same screen: the target picks, everyone else guesses what the target picked.
 *
 * Same `Split` as the writing screen, in choose mode — the gold option and the
 * rust option are the same two objects they were when they were written.
 *
 * What is deliberately absent: who wrote the decision. `TurnStarted` does not
 * carry an author, so this screen cannot leak one before the reveal.
 */

/** The design tokens key the halves; the wire uses the shared enum. */
const TO_SIDE: Record<OptionSideKey, OptionSide> = {
  [OptionSideKey.A]: OptionSide.A,
  [OptionSideKey.B]: OptionSide.B,
};

const FROM_SIDE: Record<OptionSide, OptionSideKey> = {
  [OptionSide.A]: OptionSideKey.A,
  [OptionSide.B]: OptionSideKey.B,
};

/**
 * Send just before the authoritative server deadline so the existing command
 * reaches it before its strict cutoff. At phone scale this still reads as
 * "when time runs out", without introducing a separate draft protocol.
 */
const AUTO_SUBMIT_LEAD_MS = 250;

export const Turn = () => {
  const turn = useGame((s) => s.turn);
  const players = useGame((s) => s.players);
  const myUserId = useGame((s) => s.myUserId);
  const responded = useGame((s) => s.respondedPlayers);
  const myResponse = useGame((s) => s.myResponse);
  const choose = useGame((s) => s.choose);
  const guess = useGame((s) => s.guess);

  /*
   * Selection is local until it is locked in. The server accepts exactly one
   * response per player and rejects the second, so tapping a half must not
   * send anything — otherwise the first tap is final and a mis-tap under a
   * 30-second timer is unrecoverable. Same two-step as the writing screen:
   * choose freely, then commit.
   */
  const [selected, setSelected] = useState<OptionSide | null>(null);

  // A new turn clears the previous selection. `turn` is replaced wholesale by
  // the store on every TurnStarted, so its identity is the reset signal.
  useEffect(() => {
    setSelected(null);
  }, [turn]);

  const answered = myResponse !== null || responded.includes(myUserId ?? "");
  const amTarget = turn?.target_id === myUserId;

  // A highlighted option is the fallback at timeout. Confirmation still locks
  // immediately; this only uses the existing choose/guess command if the
  // player lets the clock expire without pressing it.
  useEffect(() => {
    if (!turn || !selected || answered) return;

    const remaining =
      new Date(turn.deadline_at).getTime() -
      Date.now() -
      AUTO_SUBMIT_LEAD_MS;

    const timer = setTimeout(() => {
      play(Cue.Submit);
      if (amTarget) choose(selected);
      else guess(selected);
    }, Math.max(remaining, 0));

    return () => clearTimeout(timer);
  }, [turn, selected, answered, amTarget, choose, guess]);

  if (!turn) return null;

  const target = players.find((p) => p.user_id === turn.target_id);
  const targetName = target?.display_name ?? "";
  const targetSeed = target?.avatar_seed ?? "";

  const showing = selected ?? myResponse;

  const onChoose = (key: OptionSideKey) => {
    if (answered) return;
    setSelected(TO_SIDE[key]);
  };

  const confirm = () => {
    if (!selected || answered) return;
    if (amTarget) choose(selected);
    else guess(selected);
  };

  return (
    <div className="relative mx-auto flex h-full max-w-md flex-col overflow-hidden">
      <TimerBar
        deadlineAt={new Date(turn.deadline_at).getTime()}
        durationMs={TURN_WINDOW_MS}
      />

      {/* The page header is intentionally only progress. Who the decision is
          about belongs to the decision itself, in the persistent banner below. */}
      <div className="px-3 py-3 text-center text-[11px] uppercase tracking-widest text-ink-dim">
        {fmt(
          t.turn.turnCount,
          String(turn.turn_number),
          String(turn.total_turns),
        )}
      </div>

      <div className="relative flex-1 overflow-hidden">
        <Split
          mode="choose"
          optionA={turn.option_a}
          optionB={turn.option_b}
          onChoose={onChoose}
          chosen={showing ? FROM_SIDE[showing] : undefined}
          disabled={answered}
        />

        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-center gap-2 py-3"
          style={{ backgroundColor: `${palette.bg}D9` }}
        >
          <AvatarFace seed={targetSeed} size={22} />
          <span className="text-sm font-bold uppercase tracking-tight text-ink">
            {t.turn.guessPromptPrefix}{" "}
            <span style={{ color: nameColorFor(targetSeed, NAME_COLORS) }}>
              {targetName}
            </span>
            {t.turn.guessPromptSuffix}
          </span>
        </div>
      </div>

      {/* The same commit bar as the writing screen, edge to edge under the
          halves — the gesture that ends a decision is the same everywhere. */}
      <button
        data-sound="submit"
        disabled={!selected || answered}
        onClick={confirm}
        className="w-full py-4 text-sm font-bold uppercase tracking-widest disabled:opacity-40"
        style={{ backgroundColor: palette.ink, color: palette.bg }}
      >
        {answered ? t.turn.confirmed : t.turn.confirm}
      </button>

      <div className="flex items-center justify-between px-3 py-3">
        <span className="text-xs text-ink">
          {answered ? t.turn.locked : t.turn.pick}
        </span>
        <div className="flex gap-1.5">
          {players.map((p) => (
            <span
              key={p.user_id}
              style={{ opacity: responded.includes(p.user_id) ? 1 : 0.3 }}
            >
              <AvatarFace seed={p.avatar_seed} size={20} />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
